import type { StudioHistoryEntry } from "../../../domain/studio/visual-design.js";
import type { StudioDesignRepository, StudioDraftRecord, StudioVersionRecord, StudioVersionSummary } from "../../../repositories/studio-design-repository.js";

const DRAFT_KEY = "inspect_studio_draft";
const VERSIONS_KEY = "inspect_studio_versions";
const HISTORY_KEY = "inspect_studio_history";
const STAMPS_KEY = "inspect_studio_document_stamps";

function read<T>(key: string, fallback: T): T {
  try {
    if (typeof localStorage === "undefined") return fallback;
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

const summaryOf = ({ payload: _p, ...s }: StudioVersionRecord): StudioVersionSummary => s;

/**
 * Modo navegador (`npm run dev`, sem servidor), como todos os repositórios
 * desse modo: memória espelhada no localStorage. No modo servidor quem vale é
 * o `SqliteStudioDesignRepository` (migration 024).
 */
export class InMemoryStudioDesignRepository implements StudioDesignRepository {
  private draft: StudioDraftRecord | null = read<StudioDraftRecord | null>(DRAFT_KEY, null);
  private versions: StudioVersionRecord[] = read<StudioVersionRecord[]>(VERSIONS_KEY, []);
  private history: StudioHistoryEntry[] = read<StudioHistoryEntry[]>(HISTORY_KEY, []);
  private stamps: Record<string, number> = read<Record<string, number>>(STAMPS_KEY, {});

  async loadDraft(): Promise<StudioDraftRecord | null> {
    this.draft = read<StudioDraftRecord | null>(DRAFT_KEY, this.draft);
    return this.draft ? { ...this.draft } : null;
  }

  async saveDraftIfVersion(payload: unknown, baseVersion: number, expectedVersion: number, meta: { updatedAt: string; updatedBy: string }): Promise<boolean> {
    const current = read<StudioDraftRecord | null>(DRAFT_KEY, this.draft);
    if ((current?.version ?? 0) !== expectedVersion) return false;
    this.draft = { payload, baseVersion, version: expectedVersion + 1, updatedAt: meta.updatedAt, updatedBy: meta.updatedBy };
    write(DRAFT_KEY, this.draft);
    return true;
  }

  async listVersions(): Promise<StudioVersionSummary[]> {
    this.versions = read<StudioVersionRecord[]>(VERSIONS_KEY, this.versions);
    return [...this.versions].sort((a, b) => b.version - a.version).map(summaryOf);
  }

  async getVersion(version: number): Promise<StudioVersionRecord | null> {
    this.versions = read<StudioVersionRecord[]>(VERSIONS_KEY, this.versions);
    return this.versions.find(v => v.version === version) ?? null;
  }

  async latestVersion(): Promise<StudioVersionRecord | null> {
    this.versions = read<StudioVersionRecord[]>(VERSIONS_KEY, this.versions);
    return [...this.versions].sort((a, b) => b.version - a.version)[0] ?? null;
  }

  async appendVersion(entry: StudioVersionRecord): Promise<boolean> {
    this.versions = read<StudioVersionRecord[]>(VERSIONS_KEY, this.versions);
    if (this.versions.some(v => v.version === entry.version)) return false;
    this.versions = [...this.versions, entry];
    write(VERSIONS_KEY, this.versions);
    return true;
  }

  async appendHistory(entries: readonly StudioHistoryEntry[]): Promise<void> {
    this.history = [...read<StudioHistoryEntry[]>(HISTORY_KEY, this.history), ...entries];
    write(HISTORY_KEY, this.history);
  }

  async stampDocument(documentType: string, documentId: string, version: number): Promise<number> {
    this.stamps = read<Record<string, number>>(STAMPS_KEY, this.stamps);
    const key = `${documentType}|${documentId}`;
    if (this.stamps[key] === undefined) {
      this.stamps[key] = version;
      write(STAMPS_KEY, this.stamps);
    }
    return this.stamps[key]!;
  }

  async documentStamp(documentType: string, documentId: string): Promise<number | null> {
    this.stamps = read<Record<string, number>>(STAMPS_KEY, this.stamps);
    return this.stamps[`${documentType}|${documentId}`] ?? null;
  }

  async listHistory(limit = 500): Promise<StudioHistoryEntry[]> {
    this.history = read<StudioHistoryEntry[]>(HISTORY_KEY, this.history);
    return [...this.history].sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)).slice(0, limit);
  }
}
